import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { fetchMatches } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

export default function SearchDialog({ open, onClose }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load all matches once when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchMatches({ limit: 200 })
      .then((d) => setMatches(d))
      .finally(() => setLoading(false));
  }, [open]);

  // Reset query when closed
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return matches.slice(0, 12);
    return matches.filter((m) => {
      const haystack = [
        m.home_team?.name,
        m.away_team?.name,
        m.home_team?.short,
        m.away_team?.short,
        m.league,
        m.venue,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    }).slice(0, 30);
  }, [query, matches]);

  if (!open) return null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString(lang, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const onSelect = (id) => {
    navigate(`/matches/${id}`);
    onClose();
  };

  return (
    <div
      data-testid="search-dialog"
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[#0f0f0f] border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search className="w-5 h-5 text-white/40 shrink-0" />
          <input
            data-testid="search-input"
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent outline-none text-base text-white placeholder:text-white/30"
          />
          <button
            data-testid="search-close"
            onClick={onClose}
            className="text-white/40 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="py-10 text-center text-white/40 text-xs uppercase tracking-[0.2em]">
              {t("search.loading")}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div data-testid="search-empty" className="py-10 text-center text-white/40 text-xs uppercase tracking-[0.2em]">
              {t("search.empty")}
            </div>
          )}
          {!loading && results.length > 0 && (
            <ul data-testid="search-results" className="divide-y divide-white/5">
              {results.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => onSelect(m.id)}
                    data-testid={`search-result-${m.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="flex items-center gap-2 shrink-0">
                      {m.home_team?.logo_url ? (
                        <img src={m.home_team.logo_url} alt="" className="w-5 h-5 object-contain" />
                      ) : (
                        <span className="font-mono text-[10px] text-white/60">{m.home_team?.short}</span>
                      )}
                      <span className="text-white/40">vs</span>
                      {m.away_team?.logo_url ? (
                        <img src={m.away_team.logo_url} alt="" className="w-5 h-5 object-contain" />
                      ) : (
                        <span className="font-mono text-[10px] text-white/60">{m.away_team?.short}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">
                        {m.home_team?.name} <span className="text-white/40">vs</span> {m.away_team?.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 truncate">
                        {m.league} • {fmtDate(m.kickoff)}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {m.status === "live" && (
                        <span className="flex items-center gap-1.5 text-xs text-[#FF3B30] font-mono">
                          <span className="live-dot" />LIVE
                        </span>
                      )}
                      {m.status === "finished" && (
                        <span className="font-display text-lg tabular-nums text-white">
                          {m.score_home}-{m.score_away}
                        </span>
                      )}
                      {m.status === "upcoming" && (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">soon</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/30">
          <span>{t("search.hint")}</span>
          <span>ESC</span>
        </div>
      </div>
    </div>
  );
}
